import { generateVersion } from "../../functions/cookies";
import {
  createMovieClip,
  doubleScaleOnPortrait,
} from "../../functions/Functions_General";
import { optionalAssets } from "../../functions/helper.ts/langFilesToLoad";
import { starLog } from "../../functions/helper.ts/logger";
import { getParameterByName } from "../../getParameters";
import { linearLoadBar } from "./loadingBar";
import { loadingBackground } from "./loadingBG";
import * as Sentry from "@sentry/browser"; // Or your relevant Sentry SDK import

export class GameAssetsLoader {
  private parent: PIXI.Container;
  private logoAndLoadingBarContainer: PIXI.Container;
  private bg: loadingBackground;
  public gameFileLoader: PIXI.loaders.Loader;
  private loadProgressionBar: linearLoadBar;
  private logo: LoadingLogo;
  private spritesheetToLoad: string;
  private logoSpriteloader: PIXI.loaders.Loader;
  private fileNames: Array<Array<string>>;
  private loginFn: Function;
  // public loadArray:Array<Dictionnary>;
  private isMobile: boolean;
  private preloaderRetriesToLoad: number = 0; // Track retries for loading assets
  private preloaderMaxRetries: number = 2; // Maximum number of retries allowed
  private gameLoadRetries: number = 0;
  private maxGameLoadRetries: number = 2;

  constructor(
    parent: PIXI.Container,
    logoIndex: number,
    fileNames: Array<Array<string>>
  ) {
    // //---add game config file
    this.hideTheCanvas();
    fileNames.push([
      "commonGameConfig",
      "localization/gameConfig/gameConfigCommon.json",
    ]);
    //---
    this.parent = new PIXI.Container();
    this.logoAndLoadingBarContainer = new PIXI.Container();
    parent.addChild(this.parent);
    this.parent.addChild(this.logoAndLoadingBarContainer);
    this.bg = new loadingBackground(this.parent);
    this.loadProgressionBar = new linearLoadBar(
      this.logoAndLoadingBarContainer,
      logoIndex
    ); // create linear bar

    this.fileNames = fileNames;
    if (logoIndex === 0) {
      this.logo = new GreenIslandLoading(this.logoAndLoadingBarContainer);
      // this.spritesheetToLoad="logoRedPanda";//-----------------------------------------------set image name ! non-spritesheet one , load faster for placeholder
      this.startLoad();
    }
    if (logoIndex === 2) {
      this.logo = new starCasinoLoading(this.logoAndLoadingBarContainer);
      // this.spritesheetToLoad="logoRedPanda";//-----------------------------------------------set image name ! non-spritesheet one , load faster for placeholder
      this.startLoad();
    } else {
      this.logo = new RedPandaLoading(this.logoAndLoadingBarContainer);
      this.spritesheetToLoad = "logoRedPanda"; //-----------------------------------------------set image name ! non-spritesheet one , load faster for placeholder
      //--------------LOADER IMAGES FOR LOGO AND START ANIMATIONS

      this.logoSpriteloader = new PIXI.loaders.Loader();
      this.logoSpriteloader.defaultQueryString = "version=" + generateVersion();

      this.logoSpriteloader.add(
        "logoAnimationSpriteSheet",
        "images/" + this.spritesheetToLoad + ".json"
      );
      this.loadLogoAssets(); // Start loading logo assets
    }
    this.loadProgressionBar.parent.alpha = 1;

    this.isMobile = false;
    this.autoScaleSetup();
    // console.log('SCALE COMPLETE')
  }

  private loadLogoAssets = (): void => {
    // Handle global error (e.g., malformed JSON)
    this.logoSpriteloader.onError.once((error, loader, resource) => {
      loader; // Unused variable, but kept for consistency
      console.warn(`Logo asset load failed: ${error.message}`);
      this.handleLogoLoadFailure(resource);
    });

    this.logoSpriteloader.load((_loader, resources) => {
      const logoSheet = resources["logoAnimationSpriteSheet"];

      if (!logoSheet || logoSheet.error) {
        console.warn(
          " Logo spritesheet failed to load or is corrupt:",
          logoSheet?.error
        );
        this.handleLogoLoadFailure(logoSheet);
        return;
      }

      // SUCCESS: Continue to start the game
      this.logo.animationStart();
      this.startLoad();
    });
  };

  private handleLogoLoadFailure = (resource: any): void => {
    this.preloaderRetriesToLoad++;

    if (this.preloaderRetriesToLoad <= this.preloaderMaxRetries) {
      setTimeout(() => {
        console.log(
          `Retrying logo asset load (${this.preloaderRetriesToLoad}/${this.preloaderMaxRetries})...`
        );
        this.clearPreloaderCache();
        // Recreate loader and add asset
        this.logoSpriteloader = new PIXI.loaders.Loader();
        this.logoSpriteloader.defaultQueryString =
          "version=" + generateVersion();

        this.logoSpriteloader.add(
          "logoAnimationSpriteSheet",
          "images/" + this.spritesheetToLoad + ".json"
        );
        this.loadLogoAssets(); // Retry after small delay
      }, 1); // 1-second delay before retry
    } else {
      console.error(
        "Failed to load loading screen assets after max retries:",
        resource
      );

      // Send to Sentry
      Sentry.captureException(new Error("Loading screen asset load failure"), {
        extra: {
          retries: this.preloaderRetriesToLoad,
          resourceName: resource?.name,
          resourceUrl: resource?.url,
          error: resource?.error || "Unknown",
        },
      });

      // Show error popup to the user
      this.showErrorLoadingAssetScreen();
    }
  };

  private clearPreloaderCache = (): void => {
    for (let key in PIXI.utils.TextureCache) {
      if (key.includes(this.spritesheetToLoad)) {
        PIXI.utils.TextureCache[key].destroy(true);
        delete PIXI.utils.TextureCache[key];
      }
    }

    for (let key in PIXI.utils.BaseTextureCache) {
      if (key.includes(this.spritesheetToLoad)) {
        PIXI.utils.BaseTextureCache[key].destroy();
        delete PIXI.utils.BaseTextureCache[key];
      }
    }

    if (this.logoSpriteloader?.resources) {
      for (let key in this.logoSpriteloader.resources) {
        delete this.logoSpriteloader.resources[key];
      }
    }
  };

  private hideTheCanvas = (): void => {
    // Create a new style element
    const style = document.createElement("style");
    document.head.appendChild(style);

    // Append the CSS rule to hide the canvas initially
    style.sheet?.insertRule("canvas { visibility: hidden; }");
  };
  private showTheCanvas = (): void => {
    // Check if the style element with our specific rule already exists
    const existingStyle = Array.from(
      document.head.querySelectorAll("style")
    ).find((style) => {
      return style.textContent?.includes("canvas { visibility: visible; }");
    });

    if (!existingStyle) {
      const style = document.createElement("style");
      style.textContent = "canvas { visibility: visible; }";
      document.head.appendChild(style);
    }
  };

  private hidetheAnimationFromPreloader = (): void => {
    const body = document.querySelector("body");

    // If body exists, change its display property to default
    if (body) {
      body.style.display = "";
    }
    // Get the dot-container element
    const dotContainer = document.querySelector(
      ".dot-container"
    ) as HTMLElement;

    // If dot-container exists, change its display property to default
    if (dotContainer) {
      dotContainer.style.display = "none";
    }
    // Get all elements with the dot class
    const dots = document.querySelectorAll(".dot") as NodeListOf<HTMLElement>;

    // Iterate through each dot element
    dots.forEach((dot) => {
      // Set the animation property to 'none' to stop the animation
      dot.style.animation = "none";
    });

    // console.log('clear the prealoader');

    this.showTheCanvas();
  };

  private startLoad = (): void => {
    if (this.gameLoadRetries == 0) {
      this.hidetheAnimationFromPreloader();
    }

    // console.log("load images")
    //-----------------GAME FILES TO LOAD

    this.rebuildGameLoader(); // Rebuild loader to ensure it's fresh
    this.preloaderRetriesToLoad = 0; // Reset retries for game assets
    this.onCompleteCallBack();
  };

  private onCompleteCallBack = (): void => {
    this.tryLoadGameAssets();
  };

  private tryLoadGameAssets = (): void => {
    starLog("Loading game assets...", this.gameFileLoader);
    this.gameFileLoader.load(async (_loader: any, _resources: any) => {
      const resourceEntries = Object.entries(_resources);
      const failedAssets = resourceEntries.filter(([_, res]: any) => res.error);
      const totalAssets = resourceEntries.length;
      const succeededAssets = totalAssets - failedAssets.length;

      // Log the summary
      starLog(`Assets loaded: ${succeededAssets}/${totalAssets}`);

      // Separate critical vs allowed failures
      const criticalFailures = failedAssets.filter(
        ([key]) => !optionalAssets.has(key)
      );
      const allowedFailures = failedAssets.filter(([key]) =>
        optionalAssets.has(key)
      );

      if (allowedFailures.length > 0) {
        console.warn(
          "Non-critical assets failed to load (fallbacks will be used):",
          allowedFailures
        );
      }

      if (criticalFailures.length > 0) {
        starLog("Critical assets failed to load:", criticalFailures);

        this.gameLoadRetries++;

        if (this.gameLoadRetries <= this.maxGameLoadRetries) {
          // Retry with fresh loader instance
          setTimeout(() => {
            starLog("Retrying game asset load...");
            starLog(
              `Attempt ${this.gameLoadRetries} of ${this.maxGameLoadRetries}`
            );

            this.clearGameAssetsCache(); // <-- clear previous textures and resources
            this.rebuildGameLoader(); // recreate loader with same file list
            this.tryLoadGameAssets();
          }, 1000); // 1 second delay
        } else {
          // Final failure - log and show error
          Sentry.captureMessage(
            `Critical asset load failure after ${this.maxGameLoadRetries} attempts`,
            { extra: { failedAssets: criticalFailures } }
          );

          Sentry.captureException(new Error("Critical asset load failure"), {
            extra: { failedAssets: criticalFailures },
          });

          this.showErrorLoadingAssetScreen(); // Show modal or fallback
        }

        return; // stop here for this attempt
      }

      // SUCCESS: Continue as normal
      try {
        starLog("All critical assets loaded successfully!");
        this.loginFn();
        this.cleanUp();
      } catch (e) {
        console.error("Login function failed:", e);
        Sentry.captureException(e);
      }
    });
  };

  private clearGameAssetsCache = (): void => {
    if (this.logo) {
      this.logoAndLoadingBarContainer.removeChildren();
    }
    // Clear texture cache
    for (let key in PIXI.utils.TextureCache) {
      PIXI.utils.TextureCache[key].destroy(true); // destroy baseTexture too
      delete PIXI.utils.TextureCache[key];
    }

    // Clear base texture cache (defensive, may already be cleared above)
    for (let key in PIXI.utils.BaseTextureCache) {
      PIXI.utils.BaseTextureCache[key].destroy(); // just in case
      delete PIXI.utils.BaseTextureCache[key];
    }

    // Clear loader resources if you're reusing loader
    if (this.gameFileLoader && this.gameFileLoader.resources) {
      for (let key in this.gameFileLoader.resources) {
        delete this.gameFileLoader.resources[key];
      }
    }
  };

  // Utility: Rebuild loader to retry cleanly
  private rebuildGameLoader = (): void => {
    this.gameFileLoader = new PIXI.loaders.Loader();
    this.gameFileLoader.defaultQueryString = "version=" + generateVersion();
    if (this.fileNames === undefined || this.fileNames.length === 0) {
      console.error("No game files specified to load!");
      return;
    }
    for (let i = 0; i < this.fileNames.length; i++) {
      this.gameFileLoader.add(this.fileNames[i][0], this.fileNames[i][1]);
    }

    this.gameFileLoader.on("progress", (_loader: any, _resources: any) => {
      let loadProgress = this.gameFileLoader.progress;
      this.loadProgressionBar.update(loadProgress / 100); // % value
    });
  };

  private showErrorLoadingAssetScreen() {
    // Show fancy modal asking user to reload
    document.body.insertAdjacentHTML(
      "beforeend",
      `
  <div id="asset-error-modal" style="
    position: fixed;
    top: 0; left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  ">
    <div style="
      background: white;
      border-radius: 12px;
      padding: 24px 32px;
      text-align: center;
      max-width: 400px;
      font-family: 'Segoe UI', sans-serif;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      animation: fadeIn 0.3s ease-out;
    ">
      <h2 style="color: #e74c3c; margin-bottom: 16px;">Oops! Asset Loading Failed</h2>
      <p style="margin-bottom: 24px; font-size: 16px; color: #444;">
        Some critical game files couldn't be loaded.<br/>
        Please try reloading the page.
      </p>
      <button onclick="location.reload()" style="
        background-color: #3498db;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 10px 20px;
        font-size: 16px;
        cursor: pointer;
      ">
        Reload Page
      </button>
    </div>
  </div>
  <style>
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
  </style>
  `
    );
  }

  private cleanUp = (): void => {
    this.clearwindowListener(); //--remove the listiner
    let loadingBG = this.bg.getParent();
    loadingBG.destroy(true); //remove BG from game
    this.logo.parent.destroy(true);
    this.loadProgressionBar.parent.destroy(true);
  };
  public setLoginFn = (fn: Function): void => {
    this.loginFn = fn;
  };

  public getGameText = (): any => {
    return this.gameFileLoader.resources.lang.data;
  };
  public getGeneralText = (): any => {
    return this.gameFileLoader.resources.langGeneral.data;
  };
  public getParent = (): PIXI.Container => {
    return this.parent;
  };

  private autoScaleSetup = (): void => {
    this.isMobile = getParameterByName("mobile") == "true";
    if (this.isMobile) {
      // let self=this;
      window.addEventListener("orientationchange", this.scaleLogo, false);
      window.addEventListener("resize", this.scaleLogo, false);
    }

    this.scaleLogo();
  };

  private clearwindowListener = (): void => {
    // let self=this;
    window.removeEventListener("orientationchange", this.scaleLogo, false);
    window.removeEventListener("resize", this.scaleLogo, false);
    // console.log('CLEARED')
  };
  private scaleLogo = (): void => {
    doubleScaleOnPortrait(
      this.isMobile,
      this.logoAndLoadingBarContainer,
      [1920, 1080],
      2
    );
  };
}
//--------------------------------------------------LOADING  LOGO ANIMATION---------------------
interface LoadingLogo {
  animationStart: Function;
  parent: PIXI.Container;
}
class RedPandaLoading implements LoadingLogo {
  public parent: PIXI.Container;
  private redPanda: PIXI.Sprite;
  private redPandaAnimated: PIXI.extras.AnimatedSprite;

  constructor(parent: PIXI.Container) {
    this.parent = new PIXI.Container();
    parent.addChild(this.parent);
    this.redPanda = PIXI.Sprite.fromImage(
      "images/redPanda.png?" + "version=" + generateVersion()
    ); //empty
    this.redPanda.anchor.set(0.5);
    //logoRedPanda.scale.set(.5)
    this.redPanda.x = 1920 / 2;
    this.redPanda.y = 1080 / 2 - 12;
    this.parent.addChild(this.redPanda);
  }

  animationStart() {
    this.redPandaAnimated = createMovieClip("redPandaLogo", 0, 38);
    this.redPandaAnimated.loop = false;
    this.redPandaAnimated.anchor.set(0.5);
    this.redPandaAnimated.play();
    this.redPandaAnimated.y = -35;
    this.redPandaAnimated.animationSpeed = 0.5;
    this.redPanda.addChild(this.redPandaAnimated);
  }
  public getParent = (): PIXI.Container => {
    return this.parent;
  };
}

class GreenIslandLoading implements LoadingLogo {
  public parent: PIXI.Container;
  private greenIsland: PIXI.Sprite;

  constructor(parent: PIXI.Container) {
    this.parent = new PIXI.Container();
    parent.addChild(this.parent);
    this.greenIsland = PIXI.Sprite.fromImage(
      "images/greenIsland.png?" + "version=" + generateVersion()
    );
    this.greenIsland.anchor.set(0.5);
    this.greenIsland.x = 1920 / 2;
    this.greenIsland.y = 1080 / 2;
    this.parent.addChild(this.greenIsland);
  }

  animationStart() {}
}

class starCasinoLoading implements LoadingLogo {
  public parent: PIXI.Container;
  private greenIsland: PIXI.Sprite;

  constructor(parent: PIXI.Container) {
    this.parent = new PIXI.Container();
    parent.addChild(this.parent);
    this.greenIsland = PIXI.Sprite.fromImage(
      "images/starcasino.png?" + "version=" + generateVersion()
    );
    this.greenIsland.anchor.set(0.5);
    this.greenIsland.x = 1920 / 2;
    this.greenIsland.y = 1080 / 2;
    this.parent.addChild(this.greenIsland);
  }

  animationStart() {}
}
